import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Скелетоны загрузки.
 *
 * Это ЕДИНСТВЕННОЕ место в проекте, где разрешён `animate-pulse`: на ручные
 * плейсхолдеры в любом другом файле падает `scripts/check-skeletons.mjs`.
 * Причина не в чистоте ради чистоты — до унификации плейсхолдеры рисовались
 * тремя разными фонами (`bg-muted`, `bg-white/[0.04]`, `bg-white/[0.06]`) и
 * двумя светлыми (`bg-gray-200`, `bg-zinc-200`, белое пятно на тёмной теме),
 * а высоты подбирались на глаз, из-за чего экран прыгал при появлении данных.
 *
 * Файл ЗЕРКАЛЬНЫЙ в client-webapp, partner-webapp и partner-site — правки
 * вносить во все три (`check-skeletons.mjs` сверяет их побайтово с эталоном
 * partner-webapp). Различия тем спрятаны в CSS:
 * цвет — токен `--color-skeleton`, карточка — класс `.skeleton-card`.
 *
 * Правило показа: `isPending` (не `isLoading`), а при
 * `placeholderData: keepPreviousData` — `isPending && !data`, иначе скелетон
 * мигает поверх уже показанных данных на каждом рефетче.
 */

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `light` — для плейсхолдеров, лежащих на БЕЛОЙ подложке (QR-код рисуется на
   * белом квадрате, иначе его не считает камера). Обычный тон там сливается
   * с фоном, а не подсвечивает место будущего контента.
   */
  tone?: 'dark' | 'light';
}

/** База. Всё остальное в файле собрано из неё. */
export function Skeleton({ tone = 'dark', className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded-md',
        tone === 'light' ? 'bg-black/[0.08]' : 'bg-skeleton',
        className,
      )}
      {...props}
    />
  );
}

/** Высоты строк совпадают с типографикой экранов — не подбирать заново. */
const LINE = {
  xs: 'h-2.5', // overline, мелкие подписи
  sm: 'h-3', // вторичный текст
  base: 'h-3.5', // основной текст
  lg: 'h-4', // акцентная строка
  title: 'h-6', // заголовок секции
  hero: 'h-8', // крупное число
} as const;

export type SkeletonLineSize = keyof typeof LINE;

interface SkeletonLineProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: SkeletonLineSize;
}

/** Строка текста. Ширину задавать через className (`w-24`, `w-1/2`). */
export function SkeletonLine({ size = 'base', className, ...props }: SkeletonLineProps) {
  return <Skeleton className={cn('rounded', LINE[size], className)} {...props} />;
}

/** Ширины абзаца: неровные намеренно, ровный блок читается как таблица. */
const PARAGRAPH_WIDTHS = ['w-full', 'w-11/12', 'w-4/5', 'w-full', 'w-3/4'];

interface SkeletonParagraphProps extends React.HTMLAttributes<HTMLDivElement> {
  lines?: number;
  size?: SkeletonLineSize;
}

export function SkeletonParagraph({
  lines = 3,
  size = 'base',
  className,
  ...props
}: SkeletonParagraphProps) {
  return (
    <div className={cn('space-y-2', className)} {...props}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonLine key={i} size={size} className={PARAGRAPH_WIDTHS[i % PARAGRAPH_WIDTHS.length]} />
      ))}
    </div>
  );
}

/** Карточка-контейнер: тот же фон и рамка, что у настоящих карточек экрана. */
export function SkeletonCard({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn('skeleton-card rounded-[22px] min-h-[72px]', className)}
      {...props}
    >
      {children}
    </div>
  );
}

const COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
  itemClassName?: string;
}

/** Список одинаковых блоков (история платежей, подписки, устройства). */
export function SkeletonList({ count = 3, itemClassName, className, ...props }: SkeletonListProps) {
  return (
    <div className={cn('space-y-3', className)} {...props}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} className={cn('h-[72px]', itemClassName)} />
      ))}
    </div>
  );
}

interface SkeletonRowsProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
  /** Квадрат слева: иконка, аватар, флаг. */
  leading?: boolean;
  /** Размер этого квадрата, если он не 40×40 (`w-6 h-6`, `w-12 h-12`). */
  leadingClassName?: string;
  /** Значение справа: сумма, дата, статус. */
  trailing?: boolean;
  itemClassName?: string;
}

/** Строки «иконка — два текста — значение». Самый частый список в обоих аппах. */
export function SkeletonRows({
  count = 3,
  leading = true,
  leadingClassName,
  trailing = true,
  itemClassName,
  className,
  ...props
}: SkeletonRowsProps) {
  return (
    <div className={cn('space-y-2.5', className)} {...props}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} className={cn('flex items-center gap-3 p-3.5', itemClassName)}>
          {leading ? (
            <Skeleton className={cn('w-10 h-10 rounded-xl shrink-0', leadingClassName)} />
          ) : null}
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonLine className="w-[55%]" />
            <SkeletonLine size="sm" className="w-[35%]" />
          </div>
          {trailing ? <SkeletonLine size="lg" className="w-12 shrink-0" /> : null}
        </SkeletonCard>
      ))}
    </div>
  );
}

interface SkeletonGridProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
  cols?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * `card` — плитка сама карточка (тарифы, провайдеры оплаты).
   * `flat` — плитка лежит ВНУТРИ карточки (флаги локаций, кнопки периодов):
   * карточная поверхность дала бы вторую рамку поверх родительской.
   */
  variant?: 'card' | 'flat';
  itemClassName?: string;
}

/** Сетка плиток. */
export function SkeletonGrid({
  count = 4,
  cols = 2,
  variant = 'card',
  itemClassName,
  className,
  ...props
}: SkeletonGridProps) {
  const Item = variant === 'card' ? SkeletonCard : Skeleton;
  return (
    <div className={cn('grid gap-3', COLS[cols], className)} {...props}>
      {Array.from({ length: count }, (_, i) => (
        <Item key={i} className={cn('h-[120px]', itemClassName)} />
      ))}
    </div>
  );
}

interface SkeletonStatsProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
  cols?: 1 | 2 | 3 | 4 | 5 | 6;
  itemClassName?: string;
}

/** Ряд стат-плиток «подпись + число» (дашборд, статистика, финансы). */
export function SkeletonStats({
  count = 3,
  cols = 3,
  itemClassName,
  className,
  ...props
}: SkeletonStatsProps) {
  return (
    <div className={cn('grid gap-2.5', COLS[cols], className)} {...props}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard
          key={i}
          className={cn('min-h-0 p-3.5 space-y-2.5 rounded-[18px]', itemClassName)}
        >
          <SkeletonLine size="xs" className="w-2/3" />
          <SkeletonLine size="lg" className="w-1/2" />
        </SkeletonCard>
      ))}
    </div>
  );
}

/**
 * Обёртка экрана. Отступы совпадают с обычным экраном — иначе при появлении
 * данных содержимое сдвигается. Заголовок экран рисует сам, настоящим текстом:
 * он статичный, ждать его нечего, и переход от скелетона к данным тише.
 */
export function SkeletonPage({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-4 pb-24 space-y-4', className)} {...props}>
      {children}
    </div>
  );
}
